import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useControls } from "../lib/controls";

interface TraceLine {
  addr: string;
  text: string;
  comment?: string;
  to?: "/about" | "/projects";
}

const TRACE_LINES: TraceLine[] = [
  { addr: "0x0001", text: 'OP_CONSTANT  "Rounak"', comment: "// name" },
  { addr: "0x0002", text: "OP_GET_GLOBAL  render_hero" },
  { addr: "0x0003", text: "OP_MOUNT  #hero", comment: "// you are here" },
  { addr: "0x0004", text: "OP_CALL  about()", comment: "// see 0x01", to: "/about" },
  { addr: "0x0005", text: "OP_CALL  projects()", comment: "// see 0x02", to: "/projects" },
  { addr: "0x0006", text: "OP_RETURN" },
];

const REST_IP = 2; // the static "you are here" line
const TYPE_MS = 18;
const STEP_MS = 900;
const RETURN_PAUSE_MS = 1600;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false)
  );
}

function formatUptime(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

// The hero trace as a tiny running VM: lines type in, the instruction pointer
// loops through them, and hovering / clicking the address gutter pauses it or
// sets a breakpoint, like a debugger.
export function LiveTrace() {
  const { playSound } = useControls();
  const [reduced] = React.useState(prefersReducedMotion);

  const [bootLine, setBootLine] = React.useState(reduced ? TRACE_LINES.length : 0);
  const [bootChars, setBootChars] = React.useState(0);
  const booted = bootLine >= TRACE_LINES.length;

  const [ip, setIp] = React.useState(reduced ? REST_IP : 0);
  const [hover, setHover] = React.useState<number | null>(null);
  const [breakpoint, setBreakpoint] = React.useState<number | null>(null);
  const [cycles, setCycles] = React.useState(1024);
  const [uptime, setUptime] = React.useState(0);
  // Bumped on every IP landing so the flash animation restarts.
  const [execTick, setExecTick] = React.useState(0);

  // Boot: type each line out character by character.
  React.useEffect(() => {
    if (booted) return;
    const line = TRACE_LINES[bootLine]!;
    const t = setTimeout(
      () => {
        if (bootChars >= line.text.length) {
          setBootLine((n) => n + 1);
          setBootChars(0);
        } else {
          setBootChars((n) => n + 1);
        }
      },
      bootChars >= line.text.length ? 120 : TYPE_MS,
    );
    return () => clearTimeout(t);
  }, [booted, bootLine, bootChars]);

  // While typing, the IP follows the line being typed.
  React.useEffect(() => {
    if (!reduced && !booted) setIp(bootLine);
  }, [reduced, booted, bootLine]);

  const halted = breakpoint !== null && ip === breakpoint;
  const paused = hover !== null;

  // Run: step the IP, looping after OP_RETURN. Stops on a breakpoint or while
  // the pointer is hovering a line.
  React.useEffect(() => {
    if (reduced || !booted || halted || paused) return;
    const last = ip === TRACE_LINES.length - 1;
    const t = setTimeout(
      () => {
        setIp((last ? 0 : ip + 1));
        setCycles((c) => c + 3 + Math.floor(Math.random() * 40));
        setExecTick((n) => n + 1);
      },
      last ? RETURN_PAUSE_MS : STEP_MS,
    );
    return () => clearTimeout(t);
  }, [reduced, booted, halted, paused, ip]);

  // Uptime clock.
  React.useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => setUptime((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [reduced]);

  function toggleBreakpoint(i: number) {
    playSound("breakpoint");
    if (breakpoint === i) {
      setBreakpoint(null);
    } else {
      setBreakpoint(i);
      setIp(i);
      setExecTick((n) => n + 1);
    }
  }

  const state = halted
    ? `halted @ ${TRACE_LINES[ip]!.addr}`
    : paused
      ? "paused"
      : reduced
        ? "idle"
        : booted
          ? "running"
          : "booting";

  return (
    <>
      <div className="listing" data-no-reveal aria-live="off">
        {TRACE_LINES.map((line, i) => {
          const visible = booted || i <= bootLine;
          const text = booted || i < bootLine ? line.text : line.text.slice(0, bootChars);
          const typing = !booted && i === bootLine;
          const active = visible && ip === i;
          const isBreakpoint = breakpoint === i;
          const cls = [
            "line",
            "vm-line",
            active ? "active" : "",
            isBreakpoint ? "breakpoint" : "",
            visible ? "" : "pending",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <div
              key={line.addr}
              className={cls}
              onMouseEnter={() => booted && setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              {active && execTick > 0 && !halted ? (
                <i key={execTick} className="flash" aria-hidden="true" />
              ) : null}
              <button
                type="button"
                className="addr addr-btn"
                aria-pressed={isBreakpoint}
                aria-label={`${isBreakpoint ? "clear" : "set"} breakpoint at ${line.addr}`}
                onClick={() => toggleBreakpoint(i)}
                onFocus={() => booted && setHover(i)}
                onBlur={() => setHover(null)}
              >
                {line.addr}
              </button>
              <span>
                {text}
                {typing ? <span className="caret" aria-hidden="true" /> : null}
                {line.comment && (booted || i < bootLine) ? (
                  <span className="comment"> {line.comment}</span>
                ) : null}
                {line.to && booted ? (
                  <Link to={line.to} className="line-link" aria-label={`step into ${line.to.slice(1)}`}>
                    {" "}
                    →
                  </Link>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
      <div className="vm-status" aria-hidden="true">
        state: <b>{state}</b> · ip {TRACE_LINES[ip]!.addr} · cycles{" "}
        {cycles.toLocaleString("en-US")} · uptime {formatUptime(uptime)}
      </div>
    </>
  );
}
